import React, { useState, useEffect } from 'react';
import { Timestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Notification from './Notification';

interface Memory {
  id: string;
  userId: string;
  title: string;
  content: string;
  tags: string[];
  isPublic: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  authorName?: string;
}

interface DreamModalProps {
  memory: Memory;
  onClose: () => void;
  onDelete?: (id: string) => void;
  onEdit?: (updatedMemory: Memory) => void;
}

const DreamModal: React.FC<DreamModalProps> = ({ memory, onClose, onDelete, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMemory, setEditedMemory] = useState(memory);
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [userIcon, setUserIcon] = useState<string>('🌙');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserIcon = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', memory.userId));
        if (userDoc.exists()) {
          const icon = userDoc.data().icon || '🌙';
          console.log(`Fetched icon for user ${memory.userId}: ${icon}`);
          setUserIcon(icon);
        } else {
          console.log(`No user document found for ${memory.userId}`);
        }
      } catch (error) {
        console.error(`Error fetching user icon for ${memory.userId}:`, error);
      }
    };

    fetchUserIcon();
  }, [memory.userId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!user || user.uid !== memory.userId) return;
    setIsSaving(true);

    try {
      const dreamRef = doc(db, 'dreams', memory.id);
      const updateData = {
        title: editedMemory.title,
        content: editedMemory.content,
        tags: editedMemory.tags,
        isPublic: editedMemory.isPublic,
        updatedAt: Timestamp.now(),
        userId: memory.userId,
        authorName: memory.authorName || user.displayName || 'Anonymous Dreamer'
      };
      
      console.log('Updating dream with data:', updateData);
      await updateDoc(dreamRef, updateData);
      if (onEdit) {
        onEdit({
          ...memory,
          ...updateData,
          id: memory.id,
          createdAt: memory.createdAt
        });
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating dream:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedMemory(memory);
    setIsEditing(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !editedMemory.tags.includes(tagInput.trim())) {
      setEditedMemory({
        ...editedMemory,
        tags: [...editedMemory.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditedMemory({
      ...editedMemory,
      tags: editedMemory.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(memory.id);
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/profile/${memory.userId}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4">
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99]" 
        onClick={onClose} 
      />
      <div className="bg-dream-900/95 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6
                    border border-dream-700/30 shadow-xl animate-fade-in z-[101] relative">
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          <div 
            onClick={!isEditing ? handleUserClick : undefined}
            className={!isEditing ? "flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity" : "flex items-center gap-2 sm:gap-3"}
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-dream-700/50 border border-dream-600/30
                         flex items-center justify-center text-xl sm:text-2xl overflow-hidden">
              {userIcon}
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editedMemory.title}
                  onChange={(e) => setEditedMemory({ ...editedMemory, title: e.target.value })}
                  className="w-full px-2 py-1 text-lg sm:text-2xl font-bold rounded
                           bg-dream-800/50 text-dream-50 
                           border border-dream-700/50 
                           focus:border-dream-500 focus:ring-1 focus:ring-dream-500
                           mb-1"
                />
              ) : (
                <h2 className="text-lg sm:text-2xl font-bold text-dream-50 break-words">
                  {memory.title}
                </h2>
              )}
              <p className="text-xs sm:text-sm text-dream-300 hover:text-dream-200 transition-colors truncate">
                Dreamed by {memory.authorName || 'Anonymous Dreamer'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 -mr-1 sm:-mr-2 rounded-full hover:bg-dream-800/50 text-dream-400
                     hover:text-dream-200 transition-all duration-300 flex-shrink-0"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isEditing ? (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label htmlFor="content" className="block text-xs sm:text-sm font-medium text-dream-200 mb-2">
                Dream Content
              </label>
              <textarea
                id="content"
                value={editedMemory.content}
                onChange={(e) => setEditedMemory({ ...editedMemory, content: e.target.value })}
                rows={8}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                         bg-dream-800/50 border border-dream-700/30
                         text-sm sm:text-base text-dream-50 
                         placeholder-dream-400/50 
                         focus:outline-none focus:ring-2
                         focus:ring-dream-500/50 focus:border-dream-600/50 
                         transition-all duration-300
                         hover:border-dream-600/50 resize-none"
                placeholder="Describe your dream..."
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-dream-200 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {editedMemory.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full
                             bg-dream-700/40 text-dream-200 text-xs sm:text-sm
                             border border-dream-600/20"
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1.5 text-dream-400 hover:text-dream-200"
                    >
                      <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-1.5 text-xs sm:text-sm rounded-lg
                           bg-dream-800/50 border border-dream-700/30
                           text-dream-200 placeholder-dream-400/50
                           focus:outline-none focus:ring-2
                           focus:ring-dream-500/50 focus:border-dream-600/50
                           transition-all duration-300"
                />
                <button
                  onClick={addTag}
                  disabled={!tagInput.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                           bg-dream-700/50 text-dream-200
                           border border-dream-600/30
                           hover:bg-dream-600/50 hover:text-dream-50
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-300"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center p-3 rounded-lg bg-dream-800/50 border border-dream-700/50">
                <label htmlFor="isPublicEdit" className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="isPublicEdit"
                      checked={editedMemory.isPublic}
                      onChange={(e) => setEditedMemory({ ...editedMemory, isPublic: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`block w-12 h-7 rounded-full transition-colors duration-300 ${
                      editedMemory.isPublic ? 'bg-dream-500/50' : 'bg-dream-700/50'
                    }`}>
                      <div className={`absolute left-1 top-1 w-5 h-5 rounded-full transition-all duration-300
                                  flex items-center justify-center ${
                        editedMemory.isPublic 
                          ? 'bg-dream-500 transform translate-x-5' 
                          : 'bg-dream-700'
                      }`}>
                        {editedMemory.isPublic ? (
                          <svg className="w-3 h-3 text-dream-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-dream-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <span className="text-dream-200 text-sm">Share this dream</span>
                    <p className="text-dream-400 text-xs">
                      {editedMemory.isPublic ? 'Visible to other dreamers' : 'Only visible to you'}
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  onClick={handleCancel}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                           bg-dream-800/50 text-dream-300
                           border border-dream-700/30
                           hover:bg-dream-700/50 hover:text-dream-200
                           transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                           bg-dream-600/50 text-dream-50
                           border border-dream-500/30
                           hover:bg-dream-500/50
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-300"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <div className="prose prose-dream max-w-none">
              <p className="text-sm sm:text-base text-dream-200 whitespace-pre-wrap break-words">
                {memory.content}
              </p>
            </div>

            {memory.tags && memory.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {memory.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs sm:text-sm rounded-full
                             bg-dream-700/40 text-dream-200
                             border border-dream-600/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 pt-2">
              <div className="flex flex-col text-xs sm:text-sm text-dream-400 space-y-1">
                <div>Created: {formatDate(memory.createdAt).date} at {formatDate(memory.createdAt).time}</div>
                {memory.updatedAt && memory.updatedAt.toDate() > memory.createdAt.toDate() && (
                  <div>Last edited: {formatDate(memory.updatedAt).date} at {formatDate(memory.updatedAt).time}</div>
                )}
              </div>

              {(onEdit || onDelete) && (
                <div className="flex gap-2 sm:gap-3">
                  {onEdit && (
                    <button
                      onClick={handleEdit}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                               bg-dream-700/50 text-dream-200
                               border border-dream-600/30
                               hover:bg-dream-600/50 hover:text-dream-50
                               transition-all duration-300"
                    >
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={handleDeleteClick}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                               bg-red-900/30 text-red-300
                               border border-red-900/30
                               hover:bg-red-800/30 hover:text-red-200
                               transition-all duration-300"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
              onClick={handleDeleteCancel}
            />
            <div className="bg-dream-900/95 rounded-xl w-full max-w-sm p-4 sm:p-6
                         border border-dream-700/30 shadow-xl
                         animate-fade-in relative z-[201]">
              <h3 className="text-lg sm:text-xl font-bold text-dream-50 mb-2">Delete Dream</h3>
              <p className="text-sm sm:text-base text-dream-200 mb-4">
                Are you sure you want to delete this dream? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                           bg-dream-800/50 text-dream-300
                           border border-dream-700/30
                           hover:bg-dream-700/50 hover:text-dream-200
                           transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                           bg-red-900/30 text-red-300
                           border border-red-900/30
                           hover:bg-red-800/30 hover:text-red-200
                           transition-all duration-300"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DreamModal; 